import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../../services/auth.service';
import {ActivatedRoute} from '@angular/router';
import {
  fadeInLeftOnEnterAnimation,
  fadeInOnEnterAnimation,
  fadeInUpBigOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  fadeOutRightOnLeaveAnimation
} from 'angular-animations';
import {UserService} from '../../../services/user.service';
import {ProgressModel} from '../../../services/models/progress.model';
import {environment} from '../../../../environments/environment';
import {AnalyticsService} from '../../../services/analytics.service';
import {LeaderboardService} from '../service/leaderboard.service';
import {LeaderboardModel} from '../models/leaderboard.model';
import {NavService} from '../../../services/nav.service';

@Component({
  selector: 'app-leaderboard-veeam-page',
  templateUrl: 'leaderboard-veeam.page.html',
  styleUrls: ['leaderboard-veeam.page.scss'],
  animations: [
    fadeInOnEnterAnimation({anchor: 'enter', duration: 500}),
    fadeOutOnLeaveAnimation({anchor: 'leave', duration: 500}),
    fadeOutRightOnLeaveAnimation({anchor: 'leaveList', duration: 300}),
    fadeInLeftOnEnterAnimation({anchor: 'enterList', duration: 300}),
    fadeInUpBigOnEnterAnimation({anchor: 'enterUp', duration: 300}),
  ],
})
export class LeaderboardVeeamPage implements OnInit, OnDestroy {

  environment = null;
  menuVisible = true;

  leaderboardData: LeaderboardModel[] = null;
  leaderboardPromise: Promise<LeaderboardModel[]> = null;
  leaderboardValid = false;
  userType = null;

  progress: ProgressModel = null;
  progressPromise: Promise<ProgressModel> = null;
  progressValid = false;
  platform = 'website';

  interval = null;

  locations = {
    'AMER': {
      title: 'Regional Leaderboard - AMER',
      groups: [499]
    },
    'APJ': {
      title: 'Regional Leaderboard - APJ',
      groups: [488]
    },
    'EMEA': {
      title: 'Regional Leaderboard - EMEA',
      groups: [495]
    }
  }

  rooms = {
    'online': {
      'world': {
        title: 'Global Leaderboard',
        groups: [499, 488, 495]
      },
      'region': {
        title: 'Regional Leaderboard',
        groups: []
      },
    },
    'in-person': {
      'channel': {
        title: 'In-Person - Channel',
        groups: [527]
      },
      'field-sales': {
        title: 'In-Person - Field Sales',
        groups: [525]
      },
      'inside-sales': {
        title: 'In-Person - Inside Sales',
        groups: [526]
      },
      'renewals': {
        title: 'In-Person - Renewals',
        groups: [522]
      },
      'sdr': {
        title: 'In-Person - SDR',
        groups: [524]
      },
      'se': {
        title: 'In-Person - SE',
        groups: [523]
      },
    },
    'virtual': {
      'world': {
        title: 'Global Leaderboard',
        groups: []
      },
      'channel': {
        title: 'Channel',
        groups: [484]
      },
      'field-sales': {
        title: 'Field Sales',
        groups: [520]
      },
      'inside-sales': {
        title: 'Inside Sales',
        groups: [492]
      },
      'renewals': {
        title: 'Renewals',
        groups: [485]
      },
      'sdr': {
        title: 'SDR',
        groups: [487]
      },
      'se': {
        title: 'SE',
        groups: [486]
      },
    }
  }

  title: string = 'Global Leaderboard';
  room = 'world';
  roomIndex = 0;
  groups = [];
  onlineRooms = ['world', 'region'];
  virtualRooms = ['world', 'channel', 'field-sales', 'inside-sales', 'renewals', 'sdr', 'se'];
  inPersonRooms = ['channel', 'field-sales', 'inside-sales', 'renewals', 'sdr', 'se'];

  roomType: string = 'online';
  isRotate: boolean = false;
  isUserHighlighted: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    public authService: AuthService,
    private leaderboardService: LeaderboardService,
    public userService: UserService,
    private analytics: AnalyticsService,
    private nav: NavService,
  ) {
    this.environment = environment;
    this.roomType = this.activatedRoute.snapshot.data.roomType || 'online';
    this.isRotate = this.activatedRoute.snapshot.data.isRotate;
    this.isUserHighlighted = this.activatedRoute.snapshot.data.isUserHighlighted;
    this.roomIndex = 0;

    if (this.roomType === 'online') {
      if (this.locations.hasOwnProperty(this.userService.userInfo.addr1)) {
        this.rooms['online']['region'].groups = this.locations[this.userService.userInfo.addr1].groups;
        this.rooms['online']['region'].title = this.locations[this.userService.userInfo.addr1].title;
      } else {
        this.isRotate = false;
      }
    }

    if (this.roomType === 'in-person') {
      this.room = this.activatedRoute.snapshot.paramMap.get('room');
      if (!this.rooms[this.roomType].hasOwnProperty(this.room)) {
        this.nav.navigateHome();
        return;
      } else {
        this.groups = this.rooms[this.roomType][this.room].groups;
      }
    } else {
      if (this.roomType === 'virtual') {
        this.room = this.virtualRooms[this.roomIndex];
      } else {
        this.room = this.onlineRooms[this.roomIndex];
      }
      this.groups = this.rooms[this.roomType][this.room].groups;
    }

    this.interval = setInterval(() => {
      this.rotate();
      this.refresh()
    }, 8 * 1000 ); //5 * 60 * 1000)
  }

  ngOnInit(): void {
    this.platform = environment.platform;
  }

  ionViewWillEnter() {
    if (this.leaderboardData === null || !this.leaderboardValid) {
      if (this.isUserHighlighted && this.room !== 'world') {
        this.leaderboardPromise = this.leaderboardService.getLeaderboard();
      } else {
        this.leaderboardPromise = this.leaderboardService.getLeaderboardWorld(this.groups);
      }
    }
  }

  ionViewDidEnter() {
    this.analytics.trackView('Leaderboard');

    if (this.leaderboardData === null || !this.leaderboardValid) {
      this.leaderboardPromise.then(data => {
        this.leaderboardData = data;
        this.leaderboardValid = true;
      });
    }
  }

  rotate() {
    if (this.isRotate) {
      if (this.roomType === 'virtual') {
        this.roomIndex = (this.roomIndex + 1) % this.virtualRooms.length;
        this.room = this.virtualRooms[this.roomIndex];
        this.groups = this.rooms[this.roomType][this.room].groups;
      }

      if (this.roomType === 'online') {
        this.roomIndex = (this.roomIndex + 1) % this.onlineRooms.length;
        this.room = this.onlineRooms[this.roomIndex];
        this.groups = this.rooms[this.roomType][this.room].groups;
      }
    }
  }

  refresh() {
    if (this.isUserHighlighted && this.room !== 'world') {
      this.leaderboardPromise = this.leaderboardService.getLeaderboard();
    } else {
      this.leaderboardPromise = this.leaderboardService.getLeaderboardWorld(this.groups);
    }

    this.leaderboardPromise
      .then(data => {
        this.leaderboardData = data;
        if (this.roomType === 'virtual') {
          this.title = this.rooms[this.roomType][this.room].title;
        }
        if (this.roomType === 'online') {
          this.title = this.rooms[this.roomType][this.room].title;
        }

        this.leaderboardValid = true;
      });
  }

  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}
